import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  Tab,
  Tabs,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Snackbar,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import BuildIcon from '@mui/icons-material/Build';
import ConstructionIcon from '@mui/icons-material/Construction';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadDocumentsMachine from './UploadDocumentsMachine';
import UploadDocumentsToolsTackles from './UploadDocumentsToolsTackles';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import WorklistsService, { WORKLIST_STATUS } from './Worklists.service';
import CertificateView from './CertificateView.js';
import './Worklists.css';

const LIST_VIEW_CONFIG = {
  PENDING: { title: 'Active Worklists', status: WORKLIST_STATUS.PENDING },
  DRAFT: { title: 'Approval Queue', status: WORKLIST_STATUS.DRAFT },
  COMPLETED: { title: 'Completed Worklists', status: WORKLIST_STATUS.COMPLETED },
};

const TOP_TAB_MACHINES = 0;
const TOP_TAB_TOOLS = 1;

function StatusChip({ value }) {
  const v = (value || '').toString().toUpperCase();
  const color =
    v === 'PENDING' ? 'warning'
      : v === 'COMPLETED' ? 'success'
        : v === 'DRAFT' ? 'info'
          : v === 'REJECTED' || v === 'DECLINED' || v === 'FAILED' ? 'error'
            : 'default';
  return (
    <Chip
      size="small"
      label={value || '—'}
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 700 }}
    />
  );
}

function ApprovalChip({ value }) {
  const v = (value || '').toString().toUpperCase();
  const color =
    v === 'PENDING' ? 'warning'
      : v === 'APPROVED' || v === 'COMPLETED' ? 'success'
        : v === 'REJECTED' || v === 'DECLINED' ? 'error'
          : 'default';
  return (
    <Chip
      size="small"
      label={value || '—'}
      color={color}
      variant={color === 'default' ? 'outlined' : 'filled'}
      sx={{ fontWeight: 700 }}
    />
  );
}

function ActionSnackbar({ toast, onClose }) {
  return (
    <Snackbar
      open={!!toast?.open}
      autoHideDuration={4500}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={onClose}
        severity={toast?.severity || 'info'}
        variant="filled"
        sx={{ width: '100%', fontWeight: 600 }}
      >
        {toast?.message || ''}
      </Alert>
    </Snackbar>
  );
}

function WorklistTableRow({ item, rowIndex, listView, stateCodeByStateId, onOpenApprovalModal, onOpenRejectModal, itemApprovalState, onGenerateCertificate, onViewCertificate }) {
  const stripedBg = rowIndex % 2 === 0 ? 'background.paper' : 'grey.50';
  const itemId = item.machineWorkFlowId ?? item.machineReviewId;
  const showAction = listView === 'DRAFT' || listView === 'COMPLETED';
  const isMachineList = listView === null;
  const approvalState = itemApprovalState?.[itemId];
  const engApproved = String(item.engineerApproval || '').toUpperCase() === 'APPROVED';
  const adminApproved = String(item.adminApproval || '').toUpperCase() === 'APPROVED';
  const adminApprovedOrLocal = adminApproved || approvalState === 'approved' || approvalState === 'certGenerated';
  const certGenerated = item.isCertificateGenerated === true || approvalState === 'certGenerated';
  const isRejected = String(item.checklistReviewStatus || '').toUpperCase() === 'REJECTED';

  const renderAction = () => {
    if (isRejected) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Reject</Button>
        </Box>
      );
    }
    if (isMachineList) {
      if (!engApproved && !adminApproved) {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Approve</Button>
            <Button size="small" variant="outlined" color="error" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Reject</Button>
          </Box>
        );
      }
      if (engApproved && !adminApprovedOrLocal) {
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" onClick={() => onOpenApprovalModal?.(item)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Approve</Button>
            <Button size="small" variant="outlined" color="error" onClick={() => onOpenRejectModal?.(item)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Reject</Button>
          </Box>
        );
      }
      if (engApproved && adminApprovedOrLocal && !certGenerated) {
        return (
          <Button size="small" variant="contained" onClick={() => onGenerateCertificate?.(itemId)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>
            Generate Certificate
          </Button>
        );
      }
      if (engApproved && adminApprovedOrLocal && certGenerated) {
        return (
          <Link href="#" underline="hover" sx={{ fontWeight: 600, fontSize: '0.875rem' }} onClick={(e) => { e.preventDefault(); onViewCertificate?.(itemId); }}>
            View Certificate
          </Link>
        );
      }
      return null;
    }
    if (!showAction) return null;
    if (listView === 'COMPLETED') {
      return (
        <Link href="#" underline="hover" sx={{ fontWeight: 600, fontSize: '0.875rem' }} onClick={(e) => { e.preventDefault(); onViewCertificate?.(itemId); }}>
          View Certificate
        </Link>
      );
    }
    if (listView === 'DRAFT') {
      if (approvalState === 'certGenerated') {
        return (
          <Link href="#" underline="hover" sx={{ fontWeight: 600, fontSize: '0.875rem' }} onClick={(e) => { e.preventDefault(); onViewCertificate?.(itemId); }}>
            View Certificate
          </Link>
        );
      }
      if (approvalState === 'approved') {
        return (
          <Button size="small" variant="contained" onClick={() => onGenerateCertificate?.(itemId)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>
            Generate Certificate
          </Button>
        );
      }
      return (
        <Button size="small" variant="outlined" onClick={() => onOpenApprovalModal?.(item)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>
          Approve
        </Button>
      );
    }
    return null;
  };

  return (
    <TableRow
      className="worklist-table-row"
      sx={{
        bgcolor: stripedBg,
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{stateCodeByStateId && item.stateId != null ? (stateCodeByStateId[String(item.stateId)] ?? '—') : (item.stateCode ?? '—')}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2" fontWeight={700} color="primary">
          {item.machineReviewId ?? '—'}
        </Typography>
      </TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.companyName ?? item.companyId ?? '—'}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.siteName ?? item.siteId ?? '—'}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.machineName ?? item.machineId ?? '—'}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.engineerName ?? '—'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><ApprovalChip value={item.engineerApproval} /></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><ApprovalChip value={item.adminApproval} /></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {item.isQrMapped ? (
          <Chip size="small" icon={<QrCode2Icon sx={{ fontSize: 16 }} />} label="Yes" color="success" variant="outlined" sx={{ fontSize: '0.875rem', fontWeight: 600 }} />
        ) : (
          <Typography variant="body2" color="text.secondary" fontWeight={600}>No</Typography>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><StatusChip value={item.checklistReviewStatus} /></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.qrUniqueId ?? '—'}</TableCell>
      {(showAction || isMachineList) && <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderAction()}</TableCell>}
    </TableRow>
  );
}

function ToolsTacklesTableRow({ item, rowIndex, stateCodeByStateId, onOpenApprovalModal, onOpenRejectModal, itemApprovalState, onGenerateCertificate, onViewCertificate }) {
  const stripedBg = rowIndex % 2 === 0 ? 'background.paper' : 'grey.50';
  const itemId = item.toolsTacklesWorkFlowId ?? item.toolsTacklesReviewId;
  const approvalState = itemApprovalState?.[itemId];
  const engApproved = String(item.engineerApproval || '').toUpperCase() === 'APPROVED';
  const adminApproved = String(item.adminApproval || '').toUpperCase() === 'APPROVED';
  const adminApprovedOrLocal = adminApproved || approvalState === 'approved' || approvalState === 'certGenerated';
  const certGenerated = item.isCertificateGenerated === true || approvalState === 'certGenerated';
  const isRejected = String(item.checklistReviewStatus || '').toUpperCase() === 'REJECTED';
  const tagIdsDisplay = Array.isArray(item.tagIds) && item.tagIds.length > 0
    ? (item.tagIds.length <= 3 ? item.tagIds.join(', ') : `${item.tagIds.slice(0, 2).join(', ')} +${item.tagIds.length - 2}`)
    : '—';

  const renderAction = () => {
    if (isRejected) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Reject</Button>
        </Box>
      );
    }
    if (!engApproved && !adminApproved) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" disabled sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Reject</Button>
        </Box>
      );
    }
    if (engApproved && !adminApprovedOrLocal) {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={() => onOpenApprovalModal?.(item)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Approve</Button>
          <Button size="small" variant="outlined" color="error" onClick={() => onOpenRejectModal?.(item)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>Reject</Button>
        </Box>
      );
    }
    if (engApproved && adminApprovedOrLocal && !certGenerated) {
      return (
        <Button size="small" variant="contained" onClick={() => onGenerateCertificate?.(itemId)} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' }}>
          Generate Certificate
        </Button>
      );
    }
    if (engApproved && adminApprovedOrLocal && certGenerated) {
      return (
        <Link href="#" underline="hover" sx={{ fontWeight: 600, fontSize: '0.875rem' }} onClick={(e) => { e.preventDefault(); onViewCertificate?.(itemId); }}>
          View Certificate
        </Link>
      );
    }
    return null;
  };

  return (
    <TableRow
      className="worklist-table-row"
      sx={{
        bgcolor: stripedBg,
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{stateCodeByStateId && item.stateId != null ? (stateCodeByStateId[String(item.stateId)] ?? '—') : '—'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="body2" fontWeight={700} color="primary">
          {item.toolsTacklesReviewId ?? '—'}
        </Typography>
      </TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.companyName ?? item.companyId ?? '—'}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.siteName ?? item.siteId ?? '—'}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.toolsTacklesName ?? item.toolsTacklesId ?? '—'}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>{item.engineerName ?? '—'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><ApprovalChip value={item.engineerApproval} /></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><ApprovalChip value={item.adminApproval} /></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {item.isTagMapped ? (
          <Chip size="small" icon={<QrCode2Icon sx={{ fontSize: 16 }} />} label="Yes" color="success" variant="outlined" sx={{ fontSize: '0.875rem', fontWeight: 600 }} />
        ) : (
          <Typography variant="body2" color="text.secondary" fontWeight={600}>No</Typography>
        )}
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}><StatusChip value={item.checklistReviewStatus} /></TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{tagIdsDisplay}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>{renderAction()}</TableCell>
    </TableRow>
  );
}

const ALL_VALUE = '';

function Worklists() {
  const { userData, onLogout } = useOutletContext();
  const [listView, setListView] = useState(null);
  const [topTab, setTopTab] = useState(TOP_TAB_MACHINES);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adminMachineList, setAdminMachineList] = useState([]);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState(null);
  const [showMachineListOnly, setShowMachineListOnly] = useState(false);
  const [machineFilterState, setMachineFilterState] = useState(ALL_VALUE);
  const [machineFilterCompany, setMachineFilterCompany] = useState(ALL_VALUE);
  const [machineFilterSite, setMachineFilterSite] = useState(ALL_VALUE);
  const [machineFilterStatus, setMachineFilterStatus] = useState(ALL_VALUE);
  const [machineFilterRequestId, setMachineFilterRequestId] = useState('');
  const [showToolsTacklesListOnly, setShowToolsTacklesListOnly] = useState(false);
  const [adminToolsTacklesList, setAdminToolsTacklesList] = useState([]);
  const [adminTTLoading, setAdminTTLoading] = useState(false);
  const [adminTTError, setAdminTTError] = useState(null);
  const [ttFilterState, setTTFilterState] = useState(ALL_VALUE);
  const [ttFilterCompany, setTTFilterCompany] = useState(ALL_VALUE);
  const [ttFilterSite, setTTFilterSite] = useState(ALL_VALUE);
  const [ttFilterStatus, setTTFilterStatus] = useState(ALL_VALUE);
  const [ttFilterRequestId, setTTFilterRequestId] = useState('');
  const [filterCompany, setFilterCompany] = useState(ALL_VALUE);
  const [filterSite, setFilterSite] = useState(ALL_VALUE);
  const [approvalModalItem, setApprovalModalItem] = useState(null);
  const [approvalModalMode, setApprovalModalMode] = useState('approve'); // 'approve' | 'reject'
  const [approvalSubmitting, setApprovalSubmitting] = useState(false);
  const [approvalError, setApprovalError] = useState(null);
  const [actionToast, setActionToast] = useState({ open: false, severity: 'success', message: '' });
  const [itemApprovalState, setItemApprovalState] = useState({});
  const [certificateModalOpen, setCertificateModalOpen] = useState(false);
  const [certificateData, setCertificateData] = useState(null);
  const [certificateLoading, setCertificateLoading] = useState(false);
  const [certificateError, setCertificateError] = useState(null);
  const [certificateDownloading, setCertificateDownloading] = useState(false);
  const certificateRef = useRef(null);
  const [stateList, setStateList] = useState([]);

  const stateCodeByStateId = (stateList || []).reduce((acc, s) => {
    if (s.stateId != null) acc[String(s.stateId)] = s.stateCode ?? s.stateName ?? '—';
    return acc;
  }, {});

  const uniqueCompanies = [...new Set((list || []).map((i) => i.companyId).filter(Boolean))].sort((a, b) => a - b);
  const sitesForCompany = filterCompany === ALL_VALUE
    ? [...new Set((list || []).map((i) => i.siteId).filter(Boolean))]
    : [...new Set((list || []).filter((i) => i.companyId === filterCompany).map((i) => i.siteId).filter(Boolean))];
  const uniqueSites = [...new Set(sitesForCompany)].sort((a, b) => a - b);

  const filteredList = (list || []).filter((item) => {
    if (filterCompany !== ALL_VALUE && item.companyId !== filterCompany) return false;
    if (filterSite !== ALL_VALUE && item.siteId !== filterSite) return false;
    return true;
  });

  // Machine list view – filter options from admin list
  const machineList = adminMachineList || [];
  const uniqueStatesFromMachine = [...new Set(machineList.map((i) => i.stateId).filter((v) => v != null))].sort((a, b) => (a ?? 0) - (b ?? 0));
  const uniqueCompaniesFromMachine = [...new Set(machineList.map((i) => ({ id: i.companyId, name: i.companyName })).filter((x) => x.id != null))];
  const uniqueCompaniesById = [...new Set(uniqueCompaniesFromMachine.map((x) => x.id))].sort((a, b) => (a ?? 0) - (b ?? 0));
  const sitesForMachineCompany = machineFilterCompany === ALL_VALUE
    ? machineList.map((i) => ({ id: i.siteId, name: i.siteName })).filter((x) => x.id != null)
    : machineList.filter((i) => String(i.companyId) === String(machineFilterCompany)).map((i) => ({ id: i.siteId, name: i.siteName })).filter((x) => x.id != null);
  const uniqueSitesFromMachine = [...new Map(sitesForMachineCompany.map((s) => [s.id, s])).values()].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  const filteredMachineList = machineList.filter((item) => {
    if (machineFilterState !== ALL_VALUE && String(item.stateId) !== String(machineFilterState)) return false;
    if (machineFilterCompany !== ALL_VALUE && String(item.companyId) !== String(machineFilterCompany)) return false;
    if (machineFilterSite !== ALL_VALUE && String(item.siteId) !== String(machineFilterSite)) return false;
    if (machineFilterStatus !== ALL_VALUE && String(item.checklistReviewStatus || '').toUpperCase() !== String(machineFilterStatus).toUpperCase()) return false;
    if (machineFilterRequestId.trim() && !String(item.machineReviewId || '').toLowerCase().includes(machineFilterRequestId.trim().toLowerCase())) return false;
    return true;
  });

  const ttList = adminToolsTacklesList || [];
  const uniqueStatesFromTT = [...new Set(ttList.map((i) => i.stateId).filter((v) => v != null))].sort((a, b) => (a ?? 0) - (b ?? 0));
  const uniqueCompaniesFromTT = [...new Map(ttList.filter((i) => i.companyId != null).map((i) => [i.companyId, { id: i.companyId, name: i.companyName }])).values()];
  const uniqueCompaniesByIdTT = uniqueCompaniesFromTT.map((x) => x.id).sort((a, b) => (a ?? 0) - (b ?? 0));
  const sitesForTTCompany = ttFilterCompany === ALL_VALUE
    ? ttList.map((i) => ({ id: i.siteId, name: i.siteName })).filter((x) => x.id != null)
    : ttList.filter((i) => String(i.companyId) === String(ttFilterCompany)).map((i) => ({ id: i.siteId, name: i.siteName })).filter((x) => x.id != null);
  const uniqueSitesFromTT = [...new Map(sitesForTTCompany.map((s) => [s.id, s])).values()].sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
  const filteredToolsTacklesList = ttList.filter((item) => {
    if (ttFilterState !== ALL_VALUE && String(item.stateId) !== String(ttFilterState)) return false;
    if (ttFilterCompany !== ALL_VALUE && String(item.companyId) !== String(ttFilterCompany)) return false;
    if (ttFilterSite !== ALL_VALUE && String(item.siteId) !== String(ttFilterSite)) return false;
    if (ttFilterStatus !== ALL_VALUE && String(item.checklistReviewStatus || '').toUpperCase() !== String(ttFilterStatus).toUpperCase()) return false;
    if (ttFilterRequestId.trim() && !String(item.toolsTacklesReviewId || '').toLowerCase().includes(ttFilterRequestId.trim().toLowerCase())) return false;
    return true;
  });

  const loadAdminMachineWorklists = () => {
    setAdminLoading(true);
    setAdminError(null);
    Promise.all([
      WorklistsService.getAdminMachineWorklists(),
      WorklistsService.getStateList().catch(() => []),
    ])
      .then(([data, states]) => {
        setAdminMachineList(Array.isArray(data) ? data : []);
        setStateList(Array.isArray(states) ? states : []);
      })
      .catch((err) => {
        setAdminError(err.message || 'Failed to load machine worklists');
        setAdminMachineList([]);
      })
      .finally(() => setAdminLoading(false));
  };

  const loadAdminToolsTacklesWorklists = () => {
    setAdminTTLoading(true);
    setAdminTTError(null);
    Promise.all([
      WorklistsService.getAdminToolsTacklesWorklists(),
      WorklistsService.getStateList().catch(() => []),
    ])
      .then(([data, states]) => {
        setAdminToolsTacklesList(Array.isArray(data) ? data : []);
        setStateList(Array.isArray(states) ? states : []);
      })
      .catch((err) => {
        setAdminTTError(err.message || 'Failed to load tools & tackles worklists');
        setAdminToolsTacklesList([]);
      })
      .finally(() => setAdminTTLoading(false));
  };

  useEffect(() => {
    if (listView !== 'PENDING' && listView !== 'DRAFT' && listView !== 'COMPLETED') return;
    const status = LIST_VIEW_CONFIG[listView].status;
    setLoading(true);
    setError(null);
    WorklistsService.getWorklistByStatus(status)
      .then((data) => setList(Array.isArray(data) ? data : []))
      .catch((err) => {
        setError(err.message || 'Failed to load worklists');
        setList([]);
      })
      .finally(() => setLoading(false));
  }, [listView]);

  const handleManageClick = (view) => {
    setListView(view);
    setTopTab(TOP_TAB_MACHINES);
    setError(null);
    setFilterCompany(ALL_VALUE);
    setFilterSite(ALL_VALUE);
    setApprovalModalItem(null);
    setItemApprovalState({});
  };

  const handleBack = () => {
    setListView(null);
    setList([]);
    setError(null);
    setFilterCompany(ALL_VALUE);
    setFilterSite(ALL_VALUE);
    setApprovalModalItem(null);
    setItemApprovalState({});
  };

  const handleOpenApprovalModal = (item) => {
    setApprovalModalItem(item);
    setApprovalModalMode('approve');
  };
  const handleOpenRejectModal = (item) => {
    setApprovalModalItem(item);
    setApprovalModalMode('reject');
  };
  const showToast = (severity, message) => setActionToast({ open: true, severity, message });
  const handleCloseToast = () => setActionToast((prev) => ({ ...prev, open: false }));
  const updateMachineItemInState = (machineWorkflowId, patch) => {
    if (machineWorkflowId == null) return;
    setAdminMachineList((prev) => (prev || []).map((it) => (String(it.machineWorkFlowId) === String(machineWorkflowId) ? { ...it, ...patch } : it)));
    setList((prev) => (prev || []).map((it) => (String(it.machineWorkFlowId) === String(machineWorkflowId) ? { ...it, ...patch } : it)));
  };
  const updateToolsTacklesItemInState = (toolsTacklesWorkflowId, patch) => {
    if (toolsTacklesWorkflowId == null) return;
    setAdminToolsTacklesList((prev) => (prev || []).map((it) => (String(it.toolsTacklesWorkFlowId) === String(toolsTacklesWorkflowId) ? { ...it, ...patch } : it)));
  };
  const handleCloseApprovalModal = () => {
    setApprovalModalItem(null);
    setApprovalModalMode('approve');
    setApprovalError(null);
  };

  const handleApprove = () => {
    if (!approvalModalItem) return;
    const id = approvalModalItem.machineWorkFlowId ?? approvalModalItem.machineReviewId ?? approvalModalItem.toolsTacklesWorkFlowId ?? approvalModalItem.toolsTacklesReviewId;
    const isMachine = approvalModalItem.machineWorkFlowId != null;
    const isToolsTackles = approvalModalItem.toolsTacklesWorkFlowId != null;
    if (isMachine) {
      setApprovalSubmitting(true);
      setApprovalError(null);
      WorklistsService.approveMachineWorkflow(approvalModalItem.machineWorkFlowId)
        .then((resp) => {
          showToast('success', resp?.message || 'Workflow approved successfully');
          setItemApprovalState((prev) => ({ ...prev, [id]: 'approved' }));
          updateMachineItemInState(approvalModalItem.machineWorkFlowId, { adminApproval: 'APPROVED', checklistReviewStatus: 'COMPLETED' });
          setApprovalModalItem(null);
        })
        .catch((err) => {
          const msg = err.message || 'Failed to approve workflow';
          setApprovalError(msg);
          showToast('error', msg);
        })
        .finally(() => setApprovalSubmitting(false));
    } else if (isToolsTackles) {
      setApprovalSubmitting(true);
      setApprovalError(null);
      WorklistsService.approveToolsTacklesWorkflow(approvalModalItem.toolsTacklesWorkFlowId)
        .then((resp) => {
          showToast('success', resp?.message || 'Workflow approved successfully');
          setItemApprovalState((prev) => ({ ...prev, [id]: 'approved' }));
          updateToolsTacklesItemInState(approvalModalItem.toolsTacklesWorkFlowId, { adminApproval: 'APPROVED', checklistReviewStatus: 'COMPLETED' });
          setApprovalModalItem(null);
        })
        .catch((err) => {
          const msg = err.message || 'Failed to approve workflow';
          setApprovalError(msg);
          showToast('error', msg);
        })
        .finally(() => setApprovalSubmitting(false));
    } else {
      showToast('success', 'Approved successfully');
      setItemApprovalState((prev) => ({ ...prev, [id]: 'approved' }));
      setApprovalModalItem(null);
    }
  };

  const handleConfirmReject = () => {
    if (!approvalModalItem) return;
    const id = approvalModalItem.machineWorkFlowId ?? approvalModalItem.machineReviewId ?? approvalModalItem.toolsTacklesWorkFlowId ?? approvalModalItem.toolsTacklesReviewId;
    const isMachine = approvalModalItem.machineWorkFlowId != null;
    const isToolsTackles = approvalModalItem.toolsTacklesWorkFlowId != null;
    if (isMachine) {
      setApprovalSubmitting(true);
      setApprovalError(null);
      WorklistsService.rejectMachineWorkflow(approvalModalItem.machineWorkFlowId)
        .then((resp) => {
          showToast('success', resp?.message || 'Workflow rejected successfully');
          setItemApprovalState((prev) => ({ ...prev, [id]: 'rejected' }));
          updateMachineItemInState(approvalModalItem.machineWorkFlowId, { adminApproval: 'REJECTED', checklistReviewStatus: 'REJECTED' });
          setApprovalModalItem(null);
        })
        .catch((err) => {
          const msg = err.message || 'Failed to reject workflow';
          setApprovalError(msg);
          showToast('error', msg);
        })
        .finally(() => setApprovalSubmitting(false));
    } else if (isToolsTackles) {
      setApprovalSubmitting(true);
      setApprovalError(null);
      WorklistsService.rejectToolsTacklesWorkflow(approvalModalItem.toolsTacklesWorkFlowId)
        .then((resp) => {
          showToast('success', resp?.message || 'Workflow rejected successfully');
          setItemApprovalState((prev) => ({ ...prev, [id]: 'rejected' }));
          updateToolsTacklesItemInState(approvalModalItem.toolsTacklesWorkFlowId, { adminApproval: 'REJECTED', checklistReviewStatus: 'REJECTED' });
          setApprovalModalItem(null);
        })
        .catch((err) => {
          const msg = err.message || 'Failed to reject workflow';
          setApprovalError(msg);
          showToast('error', msg);
        })
        .finally(() => setApprovalSubmitting(false));
    } else {
      showToast('success', 'Rejected successfully');
      setItemApprovalState((prev) => ({ ...prev, [id]: 'rejected' }));
      setApprovalModalItem(null);
    }
  };

  const handleGenerateCertificate = (itemId) => {
    setCertificateModalOpen(true);
    setCertificateData(null);
    setCertificateError(null);
    setCertificateLoading(true);
    WorklistsService.getInspectionResponse()
      .then((data) => {
        setCertificateData(data);
        setItemApprovalState((prev) => ({ ...prev, [itemId]: 'certGenerated' }));
      })
      .catch((err) => setCertificateError(err.message || 'Failed to load certificate data'))
      .finally(() => setCertificateLoading(false));
  };

  const handleCloseCertificateModal = () => {
    setCertificateModalOpen(false);
    setCertificateData(null);
    setCertificateError(null);
  };

  const handleDownloadCertificate = async () => {
    if (!certificateRef.current || !certificateData) return;
    setCertificateDownloading(true);
    const el = certificateRef.current;
    try {
      el.classList.add('certificate-pdf-export');
      await new Promise((r) => requestAnimationFrame(r));
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
      const marginTopBottom = 5;
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const pdfW = pageW;
      const pdfH = pageH - marginTopBottom * 2;
      const imgW = canvas.width;
      const imgH = canvas.height;
      const ratio = Math.min(pdfW / imgW, pdfH / imgH);
      const widthMm = imgW * ratio;
      const heightMm = imgH * ratio;
      const x = (pageW - widthMm) / 2;
      const y = marginTopBottom;
      pdf.addImage(imgData, 'PNG', x, y, widthMm, heightMm);
      pdf.save(`certificate-form11-${certificateData?.occupierName?.replace(/[^a-zA-Z0-9]/g, '_') || 'certificate'}.pdf`);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      if (el) el.classList.remove('certificate-pdf-export');
      setCertificateDownloading(false);
    }
  };

  const handleViewCertificate = () => {
    setCertificateModalOpen(true);
    setCertificateData(null);
    setCertificateError(null);
    setCertificateLoading(true);
    WorklistsService.getInspectionResponse()
      .then((data) => setCertificateData(data))
      .catch((err) => setCertificateError(err.message || 'Failed to load certificate data'))
      .finally(() => setCertificateLoading(false));
  };

  const handleCompanyChange = (e) => {
    setFilterCompany(e.target.value);
    setFilterSite(ALL_VALUE);
  };

  // Upload Documents - Machine view
  if (listView === 'UPLOAD_MACHINE') {
    return (
      <div className="worklists">
        <UploadDocumentsMachine onBack={handleBack} />
        <ActionSnackbar toast={actionToast} onClose={handleCloseToast} />
      </div>
    );
  }

  // Upload Documents - Tools & Tackles view
  if (listView === 'UPLOAD_TOOLS_TACKLES') {
    return (
      <div className="worklists">
        <UploadDocumentsToolsTackles onBack={handleBack} />
        <ActionSnackbar toast={actionToast} onClose={handleCloseToast} />
      </div>
    );
  }

  // List view: Active / Approval Queue / Completed
  if (listView && listView !== 'UPLOAD') {
    return (
      <div className="worklists worklists--list">
            <Box className="worklists__topbar">
              <Box className="tabs-wrap" sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                <Tabs
                  value={topTab}
                  onChange={(_, v) => setTopTab(v)}
                  variant="fullWidth"
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    '& .MuiTabs-scroller': { overflow: 'visible !important' },
                    '& .MuiTabs-flexContainer': {
                      gap: 0,
                      backgroundColor: 'grey.100',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      padding: 1,
                      maxWidth: 420,
                      minHeight: 48,
                      alignItems: 'center',
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.9)',
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.9375rem',
                      minHeight: 40,
                      py: 0,
                      px: 2.5,
                      borderRadius: 1.5,
                      color: 'text.secondary',
                      transition: 'all 0.2s ease',
                      flex: 1,
                      maxWidth: 'none',
                    },
                    '& .MuiTab-root:hover': {
                      color: 'text.primary',
                      backgroundColor: 'rgba(255,255,255,0.5)',
                    },
                    '& .MuiTab-root.Mui-selected': {
                      color: 'primary.main',
                      backgroundColor: 'background.paper',
                      boxShadow: '0 1px 6px rgba(0,0,0,0.10)',
                      border: '1px solid',
                      borderColor: 'divider',
                    },
                    '& .MuiTabs-indicator': { display: 'none' },
                  }}
                >
                  <Tab
                    label={(
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Machines</span>
                        <Chip
                          size="small"
                          label={filteredList.length}
                          sx={{
                            height: 20,
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            bgcolor: topTab === TOP_TAB_MACHINES ? 'primary.main' : 'grey.200',
                            color: topTab === TOP_TAB_MACHINES ? '#fff' : 'text.secondary',
                          }}
                        />
                      </Box>
                    )}
                    icon={<BuildIcon sx={{ fontSize: 20, mr: 1.25 }} />}
                    iconPosition="start"
                  />
                  <Tab
                    label="Tools & Tackles"
                    icon={<ConstructionIcon sx={{ fontSize: 20, mr: 1.25 }} />}
                    iconPosition="start"
                  />
                </Tabs>
              </Box>
              <Button
                className="worklists__back"
                onClick={handleBack}
                disableElevation
                variant="contained"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 1.75,
                  py: 0.9,
                  borderRadius: 2,
                  bgcolor: 'grey.900',
                  color: '#fff',
                  flexShrink: 0,
                  ml: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.14)',
                  '&:hover': { bgcolor: 'grey.800', boxShadow: '0 8px 18px rgba(0,0,0,0.18)' },
                }}
              >
                Back to Worklists
              </Button>
            </Box>

        {loading && (
              <Box className="worklists__loading" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 10 }}>
                <CircularProgress size={44} thickness={4} sx={{ color: 'primary.main' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2.5, fontWeight: 500 }}>Loading worklists…</Typography>
              </Box>
            )}
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 0, mt: 3, borderRadius: 2 }} variant="filled">
                {error}
              </Alert>
            )}
            {!loading && !error && (
              <>
                {topTab === TOP_TAB_MACHINES && (
                  <Box className="worklists__panel">
                    {list.length > 0 && (
                      <Paper elevation={0} className="worklists__filters" sx={{ p: 1.75, mb: 2.5, mt: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: 'primary.main', bgcolor: 'background.paper', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <FilterListIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by</Typography>
                          <FormControl size="small" sx={{ minWidth: 168 }} variant="outlined">
                            <InputLabel id="worklist-filter-company">Company</InputLabel>
                            <Select labelId="worklist-filter-company" value={filterCompany} label="Company" onChange={handleCompanyChange}>
                              <MenuItem value={ALL_VALUE}>All companies</MenuItem>
                              {uniqueCompanies.map((id) => <MenuItem key={id} value={id}>Company {id}</MenuItem>)}
                            </Select>
                          </FormControl>
                          {filterCompany !== ALL_VALUE && (
                            <FormControl size="small" sx={{ minWidth: 168 }} variant="outlined">
                              <InputLabel id="worklist-filter-site">Site</InputLabel>
                              <Select labelId="worklist-filter-site" value={filterSite} label="Site" onChange={(e) => setFilterSite(e.target.value)}>
                                <MenuItem value={ALL_VALUE}>All sites</MenuItem>
                                {uniqueSites.map((id) => <MenuItem key={id} value={id}>Site {id}</MenuItem>)}
                              </Select>
                            </FormControl>
                          )}
                          {(filterCompany !== ALL_VALUE || filterSite !== ALL_VALUE) && (
                            <Button size="small" variant="text" onClick={() => { setFilterCompany(ALL_VALUE); setFilterSite(ALL_VALUE); }} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minWidth: 'auto', px: 1 }}>Clear filters</Button>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 500 }}>{filteredList.length} of {list.length}</Typography>
                        </Box>
                      </Paper>
                    )}
                    <Paper elevation={0} className="worklists__table-paper" sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                      {filteredList.length === 0 ? (
                        <Box className="worklists__empty" sx={{ py: 10, textAlign: 'center', px: 3 }}>
                          <BuildIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 2 }} />
                          <Typography variant="body1" color="text.secondary" fontWeight={500}>
                            {list.length === 0 ? 'No worklists found.' : 'No worklists match the selected filters.'}
                          </Typography>
                        </Box>
                      ) : (
                        <TableContainer>
                          <Table size="medium" aria-label="Worklists" sx={{ '& .MuiTableCell-root': { py: 1.6, px: 2 }, '& .MuiTableHead-root .MuiTableCell-root': { borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' } }}>
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'grey.100' }}>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>State</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Review Id</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Company</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Site</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Engineer</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Engineer Approval</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Admin Approval</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>QR Mapped</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                                {(listView === 'DRAFT' || listView === 'COMPLETED') && (
                                  <TableCell sx={{ fontWeight: 700, fontSize: '0.8125rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}>Action</TableCell>
                                )}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {filteredList.map((item, idx) => (
                                <WorklistTableRow
                                  key={item.machineWorkFlowId ?? item.machineReviewId}
                                  item={item}
                                  rowIndex={idx}
                                  listView={listView}
                                  stateCodeByStateId={stateCodeByStateId}
                                  onOpenApprovalModal={handleOpenApprovalModal}
                                  onOpenRejectModal={handleOpenRejectModal}
                                  itemApprovalState={itemApprovalState}
                                  onGenerateCertificate={handleGenerateCertificate}
                                  onViewCertificate={handleViewCertificate}
                                />
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Paper>
                  </Box>
                )}
                {topTab === TOP_TAB_TOOLS && (
                  <Box className="worklists__panel">
                    {list.length > 0 && (
                      <Paper elevation={0} className="worklists__filters" sx={{ p: 1.75, mb: 2.5, mt: 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: '3px solid', borderLeftColor: 'primary.main', bgcolor: 'background.paper', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          <FilterListIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by</Typography>
                          <FormControl size="small" sx={{ minWidth: 168 }} variant="outlined">
                            <InputLabel id="worklist-filter-company-tools">Company</InputLabel>
                            <Select labelId="worklist-filter-company-tools" value={filterCompany} label="Company" onChange={handleCompanyChange}>
                              <MenuItem value={ALL_VALUE}>All companies</MenuItem>
                              {uniqueCompanies.map((id) => <MenuItem key={id} value={id}>Company {id}</MenuItem>)}
                            </Select>
                          </FormControl>
                          {filterCompany !== ALL_VALUE && (
                            <FormControl size="small" sx={{ minWidth: 168 }} variant="outlined">
                              <InputLabel id="worklist-filter-site-tools">Site</InputLabel>
                              <Select labelId="worklist-filter-site-tools" value={filterSite} label="Site" onChange={(e) => setFilterSite(e.target.value)}>
                                <MenuItem value={ALL_VALUE}>All sites</MenuItem>
                                {uniqueSites.map((id) => <MenuItem key={id} value={id}>Site {id}</MenuItem>)}
                              </Select>
                            </FormControl>
                          )}
                          {(filterCompany !== ALL_VALUE || filterSite !== ALL_VALUE) && (
                            <Button size="small" variant="text" onClick={() => { setFilterCompany(ALL_VALUE); setFilterSite(ALL_VALUE); }} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem', minWidth: 'auto', px: 1 }}>Clear filters</Button>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 500 }}>{filteredList.length} of {list.length}</Typography>
                        </Box>
                      </Paper>
                    )}
                    <Paper elevation={0} sx={{ p: 6, mt: list.length > 0 ? 0 : 2.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', textAlign: 'center' }}>
                      <ConstructionIcon sx={{ fontSize: 56, color: 'action.disabled', mb: 2 }} />
                      <Typography variant="body1" color="text.secondary" fontWeight={500}>
                        Tools & Tackles worklists can be shown here when the API is available.
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </>
            )}

            <Dialog
              open={certificateModalOpen}
              onClose={handleCloseCertificateModal}
              maxWidth="md"
              fullWidth
              PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
            >
              <DialogTitle sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'primary.main', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
                Certificate (Form 11)
              </DialogTitle>
              <DialogContent sx={{ p: 0, overflow: 'auto' }}>
                {certificateLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={44} thickness={4} sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>Loading certificate…</Typography>
                  </Box>
                )}
                {certificateError && (
                  <Alert severity="error" sx={{ m: 2 }} onClose={handleCloseCertificateModal}>{certificateError}</Alert>
                )}
                {!certificateLoading && certificateData && (
                  <Box ref={certificateRef} sx={{ p: 0 }}>
                    <CertificateView data={certificateData} />
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', justifyContent: 'space-between' }}>
                <Button onClick={handleDownloadCertificate} disabled={certificateLoading || !certificateData || certificateDownloading} startIcon={<DownloadIcon />} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {certificateDownloading ? 'Downloading…' : 'Download PDF'}
                </Button>
                <Button onClick={handleCloseCertificateModal} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
              </DialogActions>
            </Dialog>

            <Dialog open={!!approvalModalItem} onClose={handleCloseApprovalModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
              <DialogTitle sx={{ fontWeight: 700, fontSize: '1.125rem', color: approvalModalMode === 'reject' ? 'error.main' : 'primary.main', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>{approvalModalMode === 'reject' ? 'Reject Worklist' : 'Approve Worklist'}</DialogTitle>
              <DialogContent sx={{ pt: 1.5 }}>
                {approvalError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setApprovalError(null)}>{approvalError}</Alert>
                )}
                {approvalModalItem && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 1.75, columnGap: 3, alignItems: 'center', pt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>ReviewId</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{approvalModalItem.machineReviewId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Company</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.companyName ?? approvalModalItem.companyId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Site</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.siteName ?? approvalModalItem.siteId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Machine</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.machineName ?? approvalModalItem.machineId ?? approvalModalItem.machineReviewId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Engineer</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.engineerName ?? '—'}</Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', justifyContent: 'center' }}>
                <Button onClick={handleCloseApprovalModal} color="inherit" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                {approvalModalMode === 'reject' ? (
                  <Button onClick={handleConfirmReject} variant="contained" color="error" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>{approvalSubmitting ? 'Rejecting…' : 'Reject'}</Button>
                ) : (
                  <Button onClick={handleApprove} variant="contained" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>{approvalSubmitting ? 'Submitting…' : 'Approve'}</Button>
                )}
              </DialogActions>
            </Dialog>
            <ActionSnackbar toast={actionToast} onClose={handleCloseToast} />
      </div>
    );
  }

  // Tools & Tackles Worklists – list-only view (after clicking Manage)
  if (showToolsTacklesListOnly) {
    return (
      <div className="worklists worklists--machine-list">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2.5,
                px: 2.5,
                py: 1.5,
                bgcolor: '#0f172a',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <ConstructionIcon sx={{ color: '#fff', fontSize: 22 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                  Tools &amp; Tackles Worklists
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={loadAdminToolsTacklesWorklists}
                  disabled={adminTTLoading}
                  startIcon={<RefreshIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: 'grey.100', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setShowToolsTacklesListOnly(false)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: 'grey.100', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
                  }}
                >
                  Back to Worklists
                </Button>
              </Box>
            </Box>
            {adminTTLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={36} thickness={4} sx={{ color: 'primary.main', mr: 2 }} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            )}
            {adminTTError && <Alert severity="error" sx={{ mb: 2 }}>{adminTTError}</Alert>}
            {!adminTTLoading && !adminTTError && adminToolsTacklesList.length > 0 && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <FilterListIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by</Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }} variant="outlined">
                    <InputLabel id="tt-filter-state">State</InputLabel>
                    <Select labelId="tt-filter-state" value={ttFilterState} label="State" onChange={(e) => setTTFilterState(e.target.value)}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      {uniqueStatesFromTT.map((id) => <MenuItem key={id} value={id}>{stateCodeByStateId?.[String(id)] ?? id}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }} variant="outlined">
                    <InputLabel id="tt-filter-company">Company</InputLabel>
                    <Select labelId="tt-filter-company" value={ttFilterCompany} label="Company" onChange={(e) => { setTTFilterCompany(e.target.value); setTTFilterSite(ALL_VALUE); }}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      {uniqueCompaniesByIdTT.map((id) => {
                        const o = uniqueCompaniesFromTT.find((x) => x.id === id);
                        return <MenuItem key={id} value={id}>{o?.name ?? `Company ${id}`}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }} variant="outlined">
                    <InputLabel id="tt-filter-site">Site</InputLabel>
                    <Select labelId="tt-filter-site" value={ttFilterSite} label="Site" onChange={(e) => setTTFilterSite(e.target.value)}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      {uniqueSitesFromTT.map((s) => <MenuItem key={s.id} value={s.id}>{s.name ?? `Site ${s.id}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }} variant="outlined">
                    <InputLabel id="tt-filter-status">Status</InputLabel>
                    <Select labelId="tt-filter-status" value={ttFilterStatus} label="Status" onChange={(e) => setTTFilterStatus(e.target.value)}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      <MenuItem value="PENDING">PENDING</MenuItem>
                      <MenuItem value="SUBMITTED">SUBMITTED</MenuItem>
                      <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                      <MenuItem value="REJECTED">REJECTED</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Request Id"
                    placeholder="Search by Request Id"
                    value={ttFilterRequestId}
                    onChange={(e) => setTTFilterRequestId(e.target.value)}
                    sx={{ minWidth: 180 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="body2" color="text.secondary">#</Typography></InputAdornment> }}
                  />
                  {(ttFilterState !== ALL_VALUE || ttFilterCompany !== ALL_VALUE || ttFilterSite !== ALL_VALUE || ttFilterStatus !== ALL_VALUE || ttFilterRequestId.trim()) && (
                    <Button size="small" variant="text" onClick={() => { setTTFilterState(ALL_VALUE); setTTFilterCompany(ALL_VALUE); setTTFilterSite(ALL_VALUE); setTTFilterStatus(ALL_VALUE); setTTFilterRequestId(''); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                      Clear filters
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 500 }}>{filteredToolsTacklesList.length} of {adminToolsTacklesList.length}</Typography>
                </Box>
              </Paper>
            )}
            {!adminTTLoading && !adminTTError && adminToolsTacklesList.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>No worklists found.</Typography>
            )}
            {!adminTTLoading && !adminTTError && adminToolsTacklesList.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                }}
              >
                <TableContainer
                  className="worklists__table-scroll"
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                  }}
                >
                  <Table
                  size="small"
                  aria-label="Tools & Tackles worklists"
                  stickyHeader
                  sx={{
                    minWidth: 1100,
                    tableLayout: 'auto',
                    '& .MuiTableCell-root': { py: 1.25, px: 2, fontSize: '0.8125rem', verticalAlign: 'middle' },
                    '& tbody .MuiTableCell-root': { borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' },
                    '& thead th': { zIndex: 1, bgcolor: 'grey.50', boxShadow: 'inset 0 -1px 0 0 rgba(0,0,0,0.08)' },
                  }}
                >
                    <TableHead>
                      <TableRow
                        sx={{
                          '& .MuiTableCell-head': {
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: 'text.primary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            py: 1.25,
                            px: 2,
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          },
                        }}
                      >
                        <TableCell>State</TableCell>
                        <TableCell>Review Id</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Site</TableCell>
                        <TableCell>Tools/Tackles</TableCell>
                        <TableCell>Engineer</TableCell>
                        <TableCell>Engineer Approval</TableCell>
                        <TableCell>Admin Approval</TableCell>
                        <TableCell>Tag Mapped</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Tag Ids</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredToolsTacklesList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={12} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                            No worklists match the selected filters.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredToolsTacklesList.map((item, idx) => (
                          <ToolsTacklesTableRow
                            key={item.toolsTacklesWorkFlowId ?? item.toolsTacklesReviewId}
                            item={item}
                            rowIndex={idx}
                            stateCodeByStateId={stateCodeByStateId}
                            onOpenApprovalModal={handleOpenApprovalModal}
                            onOpenRejectModal={handleOpenRejectModal}
                            itemApprovalState={itemApprovalState}
                            onGenerateCertificate={handleGenerateCertificate}
                            onViewCertificate={handleViewCertificate}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            <Dialog open={!!approvalModalItem} onClose={handleCloseApprovalModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
              <DialogTitle sx={{ fontWeight: 700, fontSize: '1.125rem', color: approvalModalMode === 'reject' ? 'error.main' : 'primary.main', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>{approvalModalMode === 'reject' ? 'Reject Worklist' : 'Approve Worklist'}</DialogTitle>
              <DialogContent sx={{ pt: 1.5 }}>
                {approvalError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setApprovalError(null)}>{approvalError}</Alert>
                )}
                {approvalModalItem && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 1.75, columnGap: 3, alignItems: 'center', pt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Review Id</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{approvalModalItem.toolsTacklesReviewId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Company</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.companyName ?? approvalModalItem.companyId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Site</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.siteName ?? approvalModalItem.siteId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Tools/Tackles</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.toolsTacklesName ?? approvalModalItem.toolsTacklesId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Engineer</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.engineerName ?? '—'}</Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', justifyContent: 'center' }}>
                <Button onClick={handleCloseApprovalModal} color="inherit" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                {approvalModalMode === 'reject' ? (
                  <Button onClick={handleConfirmReject} variant="contained" color="error" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>{approvalSubmitting ? 'Rejecting…' : 'Reject'}</Button>
                ) : (
                  <Button onClick={handleApprove} variant="contained" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>{approvalSubmitting ? 'Submitting…' : 'Approve'}</Button>
                )}
              </DialogActions>
            </Dialog>
            <ActionSnackbar toast={actionToast} onClose={handleCloseToast} />
      </div>
    );
  }

  // Machine Worklists – list-only view (after clicking Manage)
  if (showMachineListOnly) {
    return (
      <div className="worklists worklists--machine-list">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2.5,
                px: 2.5,
                py: 1.5,
                bgcolor: '#0f172a',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(15,23,42,0.15)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BuildIcon sx={{ color: '#fff', fontSize: 22 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', letterSpacing: '0.02em' }}>
                  Machine Worklists
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={loadAdminMachineWorklists}
                  disabled={adminLoading}
                  startIcon={<RefreshIcon />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: 'grey.100', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
                  }}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setShowMachineListOnly(false)}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                    '&:hover': { bgcolor: 'grey.100', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' },
                  }}
                >
                  Back to Worklists
                </Button>
              </Box>
            </Box>
            {adminLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={36} thickness={4} sx={{ color: 'primary.main', mr: 2 }} />
                <Typography variant="body2" color="text.secondary">Loading…</Typography>
              </Box>
            )}
            {adminError && <Alert severity="error" sx={{ mb: 2 }}>{adminError}</Alert>}
            {!adminLoading && !adminError && adminMachineList.length > 0 && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', borderLeft: '4px solid', borderLeftColor: 'primary.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <FilterListIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Filter by</Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }} variant="outlined">
                    <InputLabel id="machine-filter-state">State</InputLabel>
                    <Select labelId="machine-filter-state" value={machineFilterState} label="State" onChange={(e) => setMachineFilterState(e.target.value)}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      {uniqueStatesFromMachine.map((id) => <MenuItem key={id} value={id}>{stateCodeByStateId[String(id)] ?? id}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }} variant="outlined">
                    <InputLabel id="machine-filter-company">Company</InputLabel>
                    <Select labelId="machine-filter-company" value={machineFilterCompany} label="Company" onChange={(e) => { setMachineFilterCompany(e.target.value); setMachineFilterSite(ALL_VALUE); }}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      {uniqueCompaniesById.map((id) => {
                        const o = uniqueCompaniesFromMachine.find((x) => x.id === id);
                        return <MenuItem key={id} value={id}>{o?.name ?? `Company ${id}`}</MenuItem>;
                      })}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }} variant="outlined">
                    <InputLabel id="machine-filter-site">Site</InputLabel>
                    <Select labelId="machine-filter-site" value={machineFilterSite} label="Site" onChange={(e) => setMachineFilterSite(e.target.value)}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      {uniqueSitesFromMachine.map((s) => <MenuItem key={s.id} value={s.id}>{s.name ?? `Site ${s.id}`}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 140 }} variant="outlined">
                    <InputLabel id="machine-filter-status">Status</InputLabel>
                    <Select labelId="machine-filter-status" value={machineFilterStatus} label="Status" onChange={(e) => setMachineFilterStatus(e.target.value)}>
                      <MenuItem value={ALL_VALUE}>All</MenuItem>
                      <MenuItem value="PENDING">PENDING</MenuItem>
                      <MenuItem value="SUBMITTED">SUBMITTED</MenuItem>
                      <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                      <MenuItem value="REJECTED">REJECTED</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Request Id"
                    placeholder="Search by Request Id"
                    value={machineFilterRequestId}
                    onChange={(e) => setMachineFilterRequestId(e.target.value)}
                    sx={{ minWidth: 180 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><Typography variant="body2" color="text.secondary">#</Typography></InputAdornment> }}
                  />
                  {(machineFilterState !== ALL_VALUE || machineFilterCompany !== ALL_VALUE || machineFilterSite !== ALL_VALUE || machineFilterStatus !== ALL_VALUE || machineFilterRequestId.trim()) && (
                    <Button size="small" variant="text" onClick={() => { setMachineFilterState(ALL_VALUE); setMachineFilterCompany(ALL_VALUE); setMachineFilterSite(ALL_VALUE); setMachineFilterStatus(ALL_VALUE); setMachineFilterRequestId(''); }} sx={{ textTransform: 'none', fontWeight: 600 }}>
                      Clear filters
                    </Button>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 500 }}>{filteredMachineList.length} of {adminMachineList.length}</Typography>
                </Box>
              </Paper>
            )}
            {!adminLoading && !adminError && adminMachineList.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: 'center' }}>No worklists found.</Typography>
            )}
            {!adminLoading && !adminError && adminMachineList.length > 0 && (
              <Paper
                elevation={0}
                sx={{
                  overflow: 'hidden',
                  width: '100%',
                  maxWidth: '100%',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                }}
              >
                <TableContainer
                  className="worklists__table-scroll"
                  sx={{
                    display: 'block',
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                  }}
                >
                  <Table
                  size="small"
                  aria-label="Machine worklists"
                  stickyHeader
                  sx={{
                    minWidth: 1100,
                    tableLayout: 'auto',
                    '& .MuiTableCell-root': { py: 1.25, px: 2, fontSize: '0.8125rem', verticalAlign: 'middle' },
                    '& tbody .MuiTableCell-root': { borderBottom: '1px solid', borderColor: 'divider', textAlign: 'center' },
                    '& thead th': { zIndex: 1, bgcolor: 'grey.50', boxShadow: 'inset 0 -1px 0 0 rgba(0,0,0,0.08)' },
                  }}
                >
                    <TableHead>
                      <TableRow
                        sx={{
                          '& .MuiTableCell-head': {
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            color: 'text.primary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.8,
                            py: 1.25,
                            px: 2,
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                          },
                        }}
                      >
                        <TableCell>State</TableCell>
                        <TableCell>Review Id</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Site</TableCell>
                        <TableCell>Machine</TableCell>
                        <TableCell>Engineer</TableCell>
                        <TableCell>Engineer Approval</TableCell>
                        <TableCell>Admin Approval</TableCell>
                        <TableCell>QR Mapped</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>QR Code</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredMachineList.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={12} sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                            No worklists match the selected filters.
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredMachineList.map((item, idx) => (
                          <WorklistTableRow
                            key={item.machineWorkFlowId ?? item.machineReviewId}
                            item={item}
                            rowIndex={idx}
                            listView={null}
                            stateCodeByStateId={stateCodeByStateId}
                            onOpenApprovalModal={handleOpenApprovalModal}
                            onOpenRejectModal={handleOpenRejectModal}
                            itemApprovalState={itemApprovalState}
                            onGenerateCertificate={handleGenerateCertificate}
                            onViewCertificate={handleViewCertificate}
                          />
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 1,
                    px: 0.5,
                    color: 'text.secondary',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                >
                 
                </Typography>
              </Paper>
            )}

            {/* Certificate modal – list-only view */}
            <Dialog open={certificateModalOpen} onClose={handleCloseCertificateModal} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
              <DialogTitle sx={{ fontWeight: 700, fontSize: '1.125rem', color: 'primary.main', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>
                Certificate (Form 11)
              </DialogTitle>
              <DialogContent sx={{ p: 0, overflow: 'auto' }}>
                {certificateLoading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={44} thickness={4} sx={{ color: 'primary.main' }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>Loading certificate…</Typography>
                  </Box>
                )}
                {certificateError && (
                  <Alert severity="error" sx={{ m: 2 }} onClose={handleCloseCertificateModal}>{certificateError}</Alert>
                )}
                {!certificateLoading && certificateData && (
                  <Box ref={certificateRef} sx={{ p: 0 }}>
                    <CertificateView data={certificateData} />
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', justifyContent: 'space-between' }}>
                <Button onClick={handleDownloadCertificate} disabled={certificateLoading || !certificateData || certificateDownloading} startIcon={<DownloadIcon />} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>
                  {certificateDownloading ? 'Downloading…' : 'Download PDF'}
                </Button>
                <Button onClick={handleCloseCertificateModal} variant="contained" sx={{ textTransform: 'none', fontWeight: 600 }}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Approval modal – list-only view */}
            <Dialog open={!!approvalModalItem} onClose={handleCloseApprovalModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
              <DialogTitle sx={{ fontWeight: 700, fontSize: '1.125rem', color: approvalModalMode === 'reject' ? 'error.main' : 'primary.main', borderBottom: '1px solid', borderColor: 'divider', pb: 1.5 }}>{approvalModalMode === 'reject' ? 'Reject Worklist' : 'Approve Worklist'}</DialogTitle>
              <DialogContent sx={{ pt: 1.5 }}>
                {approvalError && (
                  <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setApprovalError(null)}>{approvalError}</Alert>
                )}
                {approvalModalItem && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 1.75, columnGap: 3, alignItems: 'center', pt: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>ReviewId</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>{approvalModalItem.machineReviewId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Company</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.companyName ?? approvalModalItem.companyId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Site</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.siteName ?? approvalModalItem.siteId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Machine</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.machineName ?? approvalModalItem.machineId ?? approvalModalItem.machineReviewId ?? '—'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700' }}>Engineer</Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>{approvalModalItem.engineerName ?? '—'}</Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', justifyContent: 'center' }}>
                <Button onClick={handleCloseApprovalModal} color="inherit" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                {approvalModalMode === 'reject' ? (
                  <Button onClick={handleConfirmReject} variant="contained" color="error" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>{approvalSubmitting ? 'Rejecting…' : 'Reject'}</Button>
                ) : (
                  <Button onClick={handleApprove} variant="contained" disabled={approvalSubmitting} sx={{ textTransform: 'none', fontWeight: 600 }}>{approvalSubmitting ? 'Submitting…' : 'Approve'}</Button>
                )}
              </DialogActions>
            </Dialog>
            <ActionSnackbar toast={actionToast} onClose={handleCloseToast} />
      </div>
    );
  }

  // Default: blocks grid
  return (
    <div className="worklists">
          <Box className="worklists__grid">
            {/* Block 1: Machine Worklists */}
            <Box sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 8px 24px rgba(15,23,42,0.12)', border: '1px solid', borderColor: 'divider', minHeight: 260, backgroundColor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ bgcolor: '#0f172a', color: '#fff', px: 3, py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Machine Worklists</Typography>
              </Box>
              <Box sx={{ p: 3, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BuildIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>All machine worklists</Typography>
                    <Typography variant="body2" color="text.secondary">Click Manage to view the list of worklists</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => {
                      setShowMachineListOnly(true);
                      if (!adminMachineList.length && !adminLoading) loadAdminMachineWorklists();
                    }}
                  >
                    Manage
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Block 2: Upload Documents - Machine */}
            <Box sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 8px 24px rgba(15,23,42,0.12)', border: '1px solid', borderColor: 'divider', minHeight: 260, backgroundColor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ bgcolor: '#0f172a', color: '#fff', px: 3, py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Upload Documents - Machine</Typography>
              </Box>
              <Box sx={{ p: 3, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BuildIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Upload documents for machines</Typography>
                    <Typography variant="body2" color="text.secondary">Search by QR Code, Request Id, or Company & Site</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => setListView('UPLOAD_MACHINE')}
                  >
                    Manage
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Block 3: Tools & Tackles Worklists */}
            <Box sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 8px 24px rgba(15,23,42,0.12)', border: '1px solid', borderColor: 'divider', minHeight: 260, backgroundColor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ bgcolor: '#0f172a', color: '#fff', px: 3, py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Tools &amp; Tackles Worklists</Typography>
              </Box>
              <Box sx={{ p: 3, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ConstructionIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>All tools &amp; tackles worklists</Typography>
                    <Typography variant="body2" color="text.secondary">Click Manage to view the list of worklists</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => {
                      setShowToolsTacklesListOnly(true);
                      if (!adminToolsTacklesList.length && !adminTTLoading) loadAdminToolsTacklesWorklists();
                    }}
                  >
                    Manage
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* Block 4: Upload Documents - Tools & Tackles */}
            <Box sx={{ borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 8px 24px rgba(15,23,42,0.12)', border: '1px solid', borderColor: 'divider', minHeight: 260, backgroundColor: 'background.paper', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ bgcolor: '#0f172a', color: '#fff', px: 3, py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Upload Documents - Tools &amp; Tackles</Typography>
              </Box>
              <Box sx={{ p: 3, bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ConstructionIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>Upload documents for tools &amp; tackles</Typography>
                    <Typography variant="body2" color="text.secondary">Search by Tag Id, Request Id, or Company &amp; Site</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => setListView('UPLOAD_TOOLS_TACKLES')}
                  >
                    Manage
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
          <ActionSnackbar toast={actionToast} onClose={handleCloseToast} />
    </div>
  );
}

export default Worklists;
